import { FC } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CheckBoxSimple } from 'shared/ui/check-box-simple'
import { Paper } from 'shared/ui/paper'

import s from './styles.module.scss'

export enum TimePeriod {
  ANY = 'any',
  MORNING = 'morning',
  DAY = 'day',
  EVENING = 'evening',
  NIGHT = 'night',
}

export interface TimePeriodValue {
  from: number
  to: number
}

const MINUTES_IN_HOUR = 60
const HOURS_IN_DAY = 24

export const MINUTES_IN_DAY = MINUTES_IN_HOUR * HOURS_IN_DAY

export const TIME_06_00 = 6 * MINUTES_IN_HOUR
export const TIME_12_00 = 12 * MINUTES_IN_HOUR
export const TIME_18_00 = 18 * MINUTES_IN_HOUR
export const TIME_23_00 = 23 * MINUTES_IN_HOUR

export const TIME_PERIODS: Record<
  Exclude<TimePeriod, TimePeriod.ANY>,
  TimePeriodValue
> = {
  [TimePeriod.MORNING]: { from: TIME_06_00, to: TIME_12_00 },
  [TimePeriod.DAY]: { from: TIME_12_00, to: TIME_18_00 },
  [TimePeriod.EVENING]: { from: TIME_18_00, to: TIME_23_00 },
  [TimePeriod.NIGHT]: { from: TIME_23_00, to: TIME_06_00 },
}

const TIME_RANGE_LABELS = {
  [TimePeriod.MORNING]: '06:00-12:00',
  [TimePeriod.DAY]: '12:00-18:00',
  [TimePeriod.EVENING]: '18:00-23:00',
  [TimePeriod.NIGHT]: '23:00-06:00',
} as const

interface Props {
  title?: string
  selectedDeparturePeriods?: TimePeriod[]
  onChangeDeparture?: (periods: TimePeriod[]) => void
}

export const TimeCheckboxGroup: FC<Props> = (props) => {
  const { t } = useTranslation()

  const { title, selectedDeparturePeriods = [], onChangeDeparture } = props

  const handlePeriodToggle = (period: TimePeriod) => {
    if (!onChangeDeparture) return

    if (period === TimePeriod.ANY) {
      onChangeDeparture([])
      return
    }

    const isCurrentlySelected = selectedDeparturePeriods.includes(period)

    if (isCurrentlySelected) {
      onChangeDeparture(selectedDeparturePeriods.filter((p) => p !== period))
    } else {
      onChangeDeparture([...selectedDeparturePeriods, period])
    }
  }

  const isAnyTimeSelected = selectedDeparturePeriods.length === 0

  const renderCheckbox = (period: TimePeriod, label: string) => {
    const isSelected =
      period === TimePeriod.ANY
        ? isAnyTimeSelected
        : selectedDeparturePeriods.includes(period)

    const timeRangeLabel =
      period !== TimePeriod.ANY ? TIME_RANGE_LABELS[period] : null

    return (
      <div
        className={s.checkboxItem}
        key={period}
      >
        <div
          className={s.checkboxLabel}
          onClick={() => handlePeriodToggle(period)}
        >
          <span className={clsx(s.labelText, 'medium-17')}>{label}</span>
          {timeRangeLabel && (
            <span className={clsx(s.timeRange, 'regular-12')}>
              {timeRangeLabel}
            </span>
          )}
        </div>

        <CheckBoxSimple
          isChecked={isSelected}
          onClick={() => handlePeriodToggle(period)}
        />
      </div>
    )
  }

  return (
    <Paper padding={[16]}>
      <div className={s.container}>
        <div className={s.header}>
          <p className={clsx('semi-bold-17', s.title)}>{title}</p>
        </div>

        <div className={s.section}>
          <div className={s.checkboxGroup}>
            {renderCheckbox(TimePeriod.ANY, t('any_time'))}
            {renderCheckbox(TimePeriod.MORNING, t('morning'))}
            {renderCheckbox(TimePeriod.DAY, t('day'))}
            {renderCheckbox(TimePeriod.EVENING, t('evening'))}
            {renderCheckbox(TimePeriod.NIGHT, t('night'))}
          </div>
        </div>
      </div>
    </Paper>
  )
}
